import React from 'react';
import { Brain, CheckCircle, Loader2, XCircle } from 'lucide-react';
import { AgentActivity } from '@/types/chat';
import { useTranslation } from 'react-i18next';
import { normalizeToolName } from '@/lib/agent/toolNameUtils';
import { isInternalAgentActivity } from '@/lib/agent/activityFilters';

function humanTitleFrom(text?: string): string {
  if (!text) return 'Working';
  const s = text.replace(/\s+/g, ' ').trim();
  const sentenceEnd = s.search(/[.!?]/);
  const first = sentenceEnd > 0 ? s.slice(0, sentenceEnd + 1) : s;
  return first.length > 80 ? first.slice(0, 77) + '…' : first;
}

function humanBodyFrom(text?: string): string | undefined {
  if (!text) return undefined;
  const s = text.replace(/\s+/g, ' ').trim();
  const sentenceEnd = s.search(/[.!?]/);
  const rest = sentenceEnd > 0 ? s.slice(sentenceEnd + 1).trim() : '';
  return rest || undefined;
}

function summarizeToolInput(input: unknown, t?: (key: string) => string): string | undefined {
  if (typeof input === 'string') {
    return input.trim();
  }
  if (input && typeof input === 'object') {
    let summary: string | undefined;
    const obj = input as Record<string, unknown>;
    const queryCaption = obj.query_caption as string | undefined;
    const queryLike = ['query', 'prompt', 'body', 'text']
      .map(key => obj[key])
      .find((value): value is string => typeof value === 'string' && value.length > 0);

    // If query_caption is a raw tool name (like "query_supabase"), translate it
    // Otherwise use it as-is
    if (typeof queryCaption === 'string' && queryCaption.trim()) {
      const trimmedCaption = queryCaption.trim();
      const normalizedCaption = normalizeToolKey(trimmedCaption);
      if (normalizedCaption && t) {
        // It's a tool name - translate it
        summary = translateToolLabel(normalizedCaption, t);
      } else {
        // Not a tool name - use as-is
        summary = trimmedCaption;
      }
    }

    // Fall back to other query-like fields
    if (!summary && typeof queryLike === 'string') {
      summary = queryLike;
    }

    // Last resort: stringify the whole input
    if (!summary) {
      try {
        return JSON.stringify(input);
      } catch {
        return String(input);
      }
    }
    return summary;
  }
  return undefined;
}

function pickThought(a: AgentActivity): string | undefined {
  const raw = a.data?.thought || a.data?.text || a.data?.summary || a.data?.message;
  if (raw == null) return undefined;
  if (typeof raw === 'string') return raw;
  try {
    return JSON.stringify(raw);
  } catch {
    return String(raw);
  }
}

function extractPlanPayload(
  a: AgentActivity,
  t?: (key: string) => string
): {
  plan?: string;
  body?: string;
  toolCalls?: { name?: string; inputSummary?: string; bodySummary?: string }[];
} {
  const payload = a.data?.thought ?? a.data;

  const coerceObject = (value: unknown): Record<string, unknown> | undefined => {
    if (!value) return undefined;
    if (typeof value === 'object' && !Array.isArray(value)) {
      return value as Record<string, unknown>;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed.startsWith('{')) {
        try {
          const parsed = JSON.parse(trimmed);
          if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
            return parsed as Record<string, unknown>;
          }
        } catch {
          return undefined;
        }
      }
    }
    return undefined;
  };

  const obj = coerceObject(payload);

  if (!obj) {
    const text = typeof payload === 'string' ? payload : undefined;
    return { plan: text };
  }

  const plan = typeof obj.plan === 'string' ? obj.plan : undefined;
  const body = typeof obj.body === 'string' ? obj.body : undefined;

  // Some backends emit a single tool_call object as the whole payload, e.g.:
  // { tool_call_id, tool_call_name, tool_call_input, tool_response, ... }
  // Treat that as a one-element toolCalls array so we can summarize it instead
  // of rendering the entire JSON blob as the plan text.
  const hasSingleToolShape =
    typeof obj.tool_call_name === 'string' ||
    obj.tool_call_input != null ||
    obj.tool_response != null;

  const rawToolCalls = Array.isArray(obj.tool_calls)
    ? obj.tool_calls
    : hasSingleToolShape
      ? [obj]
      : [];
  const toolCalls = rawToolCalls
    .map((tc: unknown) => {
      if (typeof tc !== 'object' || tc === null) return undefined;
      const toolCall = tc as Record<string, unknown>;
      const name = typeof toolCall.tool_call_name === 'string'
        ? toolCall.tool_call_name
        : typeof toolCall.name === 'string'
          ? toolCall.name
          : undefined;

      const input = toolCall.tool_call_input ?? toolCall.input ?? toolCall.arguments;
      const inputSummary = summarizeToolInput(input, t);

      const rawResponse = toolCall.tool_response ?? toolCall.response;
      let response: unknown = rawResponse;
      if (typeof rawResponse === 'string') {
        const trimmed = rawResponse.trim();
        if ((trimmed.startsWith('{') || trimmed.startsWith('['))) {
          try {
            response = JSON.parse(trimmed);
          } catch {
            response = rawResponse;
          }
        }
      }

      const toolKey = typeof name === 'string' ? normalizeToolKey(name) : undefined;

      let bodySummary: string | undefined;
      if (response && typeof response === 'object') {
        // Try a tool-specific concise summary first.
        bodySummary = summarizeToolResponse(toolKey, response);
        if (!bodySummary) {
          const responseObj = response as Record<string, unknown>;
          const result =
            (responseObj.result as string | undefined) ||
            (responseObj.summary as string | undefined) ||
            (responseObj.text as string | undefined);
          if (typeof result === 'string') {
            bodySummary = result;
          } else {
            try {
              bodySummary = JSON.stringify(response);
            } catch {
              bodySummary = String(response);
            }
          }
        }
      } else if (typeof response === 'string') {
        bodySummary = response;
      }

      if (!name && !inputSummary && !bodySummary) return undefined;
      return { name, inputSummary, bodySummary };
    })
    .filter(Boolean) as { name?: string; inputSummary?: string; bodySummary?: string }[];

  return { plan, body, toolCalls: toolCalls.length ? toolCalls : undefined };
}

// Tool keys that should be displayed to users
const SUPPORTED_TOOL_KEYS = [
  'lead_gen_v9',
  'google_instagram_search',
  'google_ig_tt_yt_search',
  'create_post',
  'query_supabase_v2',
  'scrape_tiktok_profile',
  'scrape_instagram_profile',
  'scrape_youtube_profile',
  'link_captioner',
  'cobalt_link',
  'fetch_youtube_video_detail',
  'get_schema_and_table',
  'query_database',
  'fetch_audience',
  'deep_statistics_agent',
  'query_caption',
] as const;

function normalizeToolKey(raw?: string): string | undefined {
  if (!raw) return undefined;
  let s = raw.trim().toLowerCase();
  // Some backends prefix tool labels with "CALL ", e.g. "CALL google_instagram_search".
  // Strip that prefix so we can match the underlying tool name against our
  // known keys and aliases.
  if (s.startsWith('call ')) {
    s = s.slice(5).trim();
  }
  // Some backends aggregate tool names with semicolons, e.g.
  // "query_supabase_v3;query_supabase_v3". Keep only the first token.
  if (s.includes(';')) {
    s = s.split(';')[0]?.trim() || s;
  }
  if (!s) return undefined;

  // Use shared normalization utility
  const { normalized, versionNormalized, versionStripped } = normalizeToolName(s);

  // Try exact match first (e.g., lead_gen_v9), then versioned form (query_supabase_v2), then stripped (fetch_audience)
  if ((SUPPORTED_TOOL_KEYS as readonly string[]).includes(normalized)) return normalized;
  if ((SUPPORTED_TOOL_KEYS as readonly string[]).includes(versionNormalized)) return versionNormalized;
  if ((SUPPORTED_TOOL_KEYS as readonly string[]).includes(versionStripped)) return versionStripped;

  // Handle common aliases
  if (s === 'lead-gen-v9-google') return 'lead_gen_v9';
  if (s === 'google-instagram-search') return 'google_instagram_search';
  if (s === 'post creation agent') return 'create_post';
  if (s === 'query database') return 'query_supabase_v2';
  if (s === 'scrape_ig_profile') return 'scrape_instagram_profile';
  if (normalized === 'get_schema_and_table') return 'get_schema_and_table';
  if (s === 'fetching audience' || normalized === 'fetching_audience') return 'fetch_audience';

  if (process.env.NODE_ENV === 'development') {
    console.warn(`[AgentStepsTimeline] Unmapped tool: "${raw}" (normalized="${normalized}", versionNorm="${versionNormalized}", stripped="${versionStripped}")`);
  }

  return undefined;
}

function translateToolLabel(toolKey: string, t: (key: string) => string): string {
  const translationKey = `agent_tools.${toolKey}`;
  const translated = t(translationKey);

  // If translation returns the key itself (not found), return a fallback
  if (translated === translationKey) {
    return toolKey.replace(/_/g, ' ');
  }

  return translated;
}

function summarizeToolResponse(toolKey: string | undefined, response: unknown): string | undefined {
  if (!toolKey || !response || typeof response !== 'object') return undefined;

  // Helper to safely pull first item from common shapes
  const firstItem = (obj: unknown): unknown => {
    if (!obj || typeof obj !== 'object') return undefined;
    if (Array.isArray(obj)) return obj[0];
    const objRecord = obj as Record<string, unknown>;
    if (Array.isArray(objRecord.items)) return objRecord.items[0];
    return obj;
  };

  if (toolKey === 'scrape_instagram_profile') {
    const profile = firstItem(response);
    if (!profile || typeof profile !== 'object') return undefined;
    const profileObj = profile as Record<string, unknown>;
    const userObj = (typeof profileObj.user === 'object' && profileObj.user !== null) ? profileObj.user as Record<string, unknown> : undefined;
    const username = profileObj.username || userObj?.username;
    const followers = profileObj.followersCount ?? profileObj.followers ?? profileObj.follower_count;
    const posts = profileObj.postsCount ?? profileObj.mediaCount ?? profileObj.media_count;
    const bio = typeof profileObj.biography === 'string' ? profileObj.biography : undefined;

    const parts: string[] = [];
    if (username) parts.push(`@${username}`);
    if (typeof followers === 'number') parts.push(`${followers} followers`);
    if (typeof posts === 'number') parts.push(`${posts} posts`);
    let summary = parts.join(' • ');
    if (!summary && bio) {
      summary = humanTitleFrom(bio);
    } else if (summary && bio) {
      const bioTitle = humanTitleFrom(bio);
      if (bioTitle && !summary.includes(bioTitle)) {
        summary += ` — ${bioTitle}`;
      }
    }
    return summary || undefined;
  }

  if (toolKey === 'scrape_tiktok_profile') {
    const profile = firstItem(response);
    if (!profile || typeof profile !== 'object') return undefined;
    const profileObj = profile as Record<string, unknown>;
    const userObj = (typeof profileObj.user === 'object' && profileObj.user !== null) ? profileObj.user as Record<string, unknown> : undefined;
    const username = profileObj.username || profileObj.uniqueId || userObj?.username;
    const followers = profileObj.followers ?? profileObj.followersCount ?? profileObj.followerCount;
    const likes = profileObj.likes ?? profileObj.likesCount ?? profileObj.heartCount;

    const parts: string[] = [];
    if (username) parts.push(`@${username}`);
    if (typeof followers === 'number') parts.push(`${followers} followers`);
    if (typeof likes === 'number') parts.push(`${likes} likes`);
    const summary = parts.join(' • ');
    return summary || undefined;
  }

  if (toolKey === 'link_captioner') {
    const responseObj = response as Record<string, unknown>;
    const items = Array.isArray(responseObj.items) ? responseObj.items : Array.isArray(response) ? response : [];
    const first = (typeof items[0] === 'object' && items[0] !== null) ? items[0] as Record<string, unknown> : undefined;
    const caption = first?.caption || first?.summary || first?.text;
    if (typeof caption === 'string' && caption.trim()) {
      return humanTitleFrom(caption);
    }
    if (items.length > 0) {
      return `${items.length} media item(s) summarized`;
    }
    return undefined;
  }

  if (toolKey === 'google_instagram_search') {
    const responseObj = response as Record<string, unknown>;
    const totalQueries = responseObj.total_queries ?? responseObj.totalQueries;
    const successful = responseObj.successful_queries ?? responseObj.successfulQueries;
    if (typeof totalQueries === 'number' || typeof successful === 'number') {
      const parts: string[] = [];
      if (typeof totalQueries === 'number') parts.push(`${totalQueries} queries`);
      if (typeof successful === 'number') parts.push(`${successful} succeeded`);
      return parts.join(', ') || undefined;
    }
    return undefined;
  }

  // For other tools, fall back to generic summarization.
  return undefined;
}

function getActivityToolKey(a: AgentActivity): string | undefined {
  const rawToolName = (typeof a.data?.tool_name === 'string' && a.data.tool_name) || (typeof a.label === 'string' && a.label) || '';
  return normalizeToolKey(rawToolName);
}

function dedupeToolActivitiesByKey(activities: AgentActivity[]): AgentActivity[] {
  type Bucket = { firstIndex: number; items: AgentActivity[] };
  type IndexedActivity = { index: number; activity: AgentActivity };
  const buckets = new Map<string, Bucket>();
  const mergedActivities: IndexedActivity[] = [];

  activities.forEach((activity, index) => {
    const toolKey = getActivityToolKey(activity);

    // Preserve unmapped tools instead of dropping them
    if (!toolKey) {
      mergedActivities.push({ index, activity });
      return;
    }

    // Keep only one timeline card per tool label/key.
    // Repeated invocations of the same tool should update that card instead
    // of creating additional cards (e.g., "Fetching Data").
    const existing = buckets.get(toolKey);
    if (!existing) {
      buckets.set(toolKey, { firstIndex: index, items: [activity] });
      return;
    }
    existing.items.push(activity);
  });

  const dedupedMapped = [...buckets.entries()]
    .sort(([, a], [, b]) => a.firstIndex - b.firstIndex)
    .map(([, bucket]) => {
      const byTime = [...bucket.items].sort((a, b) => {
        const ta = typeof a.timestamp === 'number' ? a.timestamp : 0;
        const tb = typeof b.timestamp === 'number' ? b.timestamp : 0;
        return ta - tb;
      });
      const latest = byTime[byTime.length - 1]!;

      return { index: bucket.firstIndex, activity: { ...latest } };
    });

  // Merge mapped and unmapped activities back in deterministic original order.
  return [...dedupedMapped, ...mergedActivities]
    .sort((a, b) => a.index - b.index)
    .map(item => item.activity);
}

const translateThinkingLabel = (label: string, t: (key: string) => string): string => {
  const normalized = label.toLowerCase().trim();

  const labelMap: Record<string, string> = {
    'analyzing user question': 'agent_thinking.analyzing_user_question',
    'applying socmed best practices': 'agent_thinking.applying_socmed_best_practices',
    'researching the platform': 'agent_thinking.researching_platform',
    'diving to data': 'agent_thinking.diving_to_data',
    'thinking': 'agent_thinking.thinking',
    'planning': 'agent_thinking.planning',
    'understanding': 'agent_thinking.understanding',
    'processing': 'agent_thinking.processing',
    'working': 'agent_thinking.working',
  };

  const key = labelMap[normalized];
  if (key) {
    const translated = t(key);
    // If translation is found, use it; otherwise fallback to English defaults
    if (translated !== key) return translated;
    // Use t() with fallback values for consistency
    return t(key, getFallbackLabel(normalized));
  }

  const translated = t('agent_thinking.agent');
  return translated === 'agent_thinking.agent' ? 'Agent' : translated;
};

function getFallbackLabel(normalized: string): string {
  const fallbacks: Record<string, string> = {
    'analyzing user question': 'Analyzing User Question',
    'applying socmed best practices': 'Applying Socmed Best Practices',
    'researching the platform': 'Researching the Platform',
    'diving to data': 'Diving to Data',
    'thinking': 'Thinking...',
    'planning': 'Planning...',
    'understanding': 'Understanding...',
    'processing': 'Processing...',
    'working': 'Working...',
  };
  return fallbacks[normalized] || 'Agent';
}

export const AgentStepsTimeline: React.FC<{ activities: AgentActivity[]; showDraftingCard?: boolean }> = ({
  activities,
  showDraftingCard = false,
}) => {
  const { t } = useTranslation();
  const translatedAgentTitle = t('agent_thinking.agent');
  const agentTitle = translatedAgentTitle === 'agent_thinking.agent' ? 'Agent' : translatedAgentTitle;

  if (!activities || activities.length === 0) return null;

  const visibleActivities = activities.filter(a => !isInternalAgentActivity(a));
  const source = visibleActivities.length > 0 ? visibleActivities : activities;

  // Render all explicit tool-call/search activities so the pending/thinking
  // card handoff is immediate when tools start streaming in.
  const toolCalls = source.filter(a => a.type === 'tool_call' || a.type === 'search');

  // Collect IDs of tools already represented by explicit tool_call activities
  // so we don't duplicate them when synthesizing from thinking payloads.
  const explicitToolKeys = new Set(
    toolCalls.map(a => getActivityToolKey(a)).filter(Boolean),
  );

  // Also extract tool calls embedded in thinking payloads and synthesize
  // activities for any mapped tools not already covered by explicit events.
  const syntheticFromThinking: AgentActivity[] = [];
  for (const thinking of [...source].reverse()) {
    if (thinking.type !== 'thinking' || !pickThought(thinking)) continue;
    const { toolCalls: planToolCalls } = extractPlanPayload(thinking, t);
    if (!planToolCalls || planToolCalls.length === 0) continue;

    for (let index = 0; index < planToolCalls.length; index++) {
      const tc = planToolCalls[index];
      const rawName = tc.name || '';
      const toolKey = normalizeToolKey(rawName);
      if (!toolKey) continue;
      if (explicitToolKeys.has(toolKey)) continue;
      explicitToolKeys.add(toolKey);

      syntheticFromThinking.push({
        type: 'tool_call',
        status: thinking.status,
        label: rawName,
        timestamp: thinking.timestamp,
        data: {
          id: `${thinking.data?.id || 'plan-tool'}-${index}`,
          position: thinking.data?.position,
          tool_name: rawName,
          tool_key: toolKey,
          tool_input: tc.inputSummary,
          tool_response: tc.bodySummary,
          raw: {
            from_plan: true,
            tool_key: toolKey,
            original: thinking.data,
          },
        },
      } as AgentActivity);
    }
    break; // only process the latest thinking with tool_calls
  }

  const allToolItems = dedupeToolActivitiesByKey([...toolCalls, ...syntheticFromThinking]);

  let items: AgentActivity[] = [];

  if (allToolItems.length > 0) {
    const latestThinking = [...source]
      .reverse()
      .find(a => a.type === 'thinking' && !!pickThought(a));

    const hasError = allToolItems.some(a => a.status === 'error');
    const hasRunning = allToolItems.some(a => a.status === 'running');
    const agentStatus: AgentActivity['status'] = hasError
      ? 'error'
      : hasRunning
        ? 'running'
        : 'success';

    const agentCard: AgentActivity = latestThinking
      ? {
          ...latestThinking,
          status: agentStatus,
          label: agentTitle,
          data: {
            ...(latestThinking.data || {}),
            force_agent_title: true,
            hide_plan_body: true,
          },
        }
      : {
          type: 'thinking',
          status: agentStatus,
          label: agentTitle,
          data: {
            force_agent_title: true,
            hide_plan_body: true,
          },
        } as AgentActivity;

    items = [agentCard, ...allToolItems];
  } else {
    const latestThinking = [...source]
      .reverse()
      .find(a => a.type === 'thinking' && !!pickThought(a));

    if (!latestThinking) return null;
    items = [latestThinking];
  }

  return (
    <div className="space-y-2">
      {items.map((a, i) => {
        const thought = pickThought(a);

        let baseTitle: string;
        let body: string | undefined;
        let isAgentCard = false;

        const done = a.status === 'success';
        const failed = a.status === 'error';

        if (a.type === 'tool_call' || a.type === 'search') {
          const key = getActivityToolKey(a);
          if (key) {
            baseTitle = translateToolLabel(key, t);
          } else {
            const fallbackLabel = (a.data?.tool_name as string | undefined) || a.label;
            baseTitle = fallbackLabel
              ? fallbackLabel.replace(/^CALL\s+/i, '').trim()
              : t('agent_tools.tool_call');
          }
          // Display tool input when available and not done
          if (!done) {
            body = summarizeToolInput(a.data?.tool_input, t);
          }
        } else {
          const forceAgentTitle = !!a.data?.force_agent_title;
          const hidePlanBody = !!a.data?.hide_plan_body;
          // For thinking type, check if we have a label to translate
          const rawLabel = (a.data?.node_title as string | undefined) || a.label;
          if (forceAgentTitle) {
            baseTitle = agentTitle;
            isAgentCard = true;
          } else if (rawLabel && a.type === 'thinking') {
            baseTitle = translateThinkingLabel(rawLabel, t);
            if (baseTitle === agentTitle) isAgentCard = true;
          } else {
            baseTitle = rawLabel || agentTitle;
            if (baseTitle === agentTitle) isAgentCard = true;
          }

          const { plan, body: explicitBody, toolCalls } = extractPlanPayload(a, t);

          // When the workflow is still running, show a concise but informative
          // Plan body. Once the activity is marked as success, collapse the
          // body so only the Plan header row remains instead of a wall of text.
          if (!done && !hidePlanBody) {
            const sections: string[] = [];

            const hasToolCalls = !!toolCalls && toolCalls.length > 0;

            // Prefer explicit plan text when present. Only fall back to the raw
            // thought string when there are no structured toolCalls, to avoid
            // dumping huge JSON blobs into the Plan card when the payload is a
            // single tool_call object.
            const planText = plan ?? (!hasToolCalls ? thought : undefined);
            if (planText) {
              sections.push(planText.trim());
            }

            // Skip showing tool details in the Plan body if we're rendering
            // them as separate tool cards (items contains synthetic tool_call activities).
            // This prevents duplicate display and keeps the Plan card concise.

            if (explicitBody && explicitBody.trim().length > 0) {
              sections.push(explicitBody.trim());
            }

            body = sections.join('\n\n').trim() || undefined;
          } else {
            body = undefined;
          }
        }
        return (
          <div key={i} className="flex items-start space-x-2 bg-neutral-100 dark:bg-neutral-800 rounded-lg px-3 py-2">
            {isAgentCard ? (
              <Brain className="h-4 w-4 text-purple-500 mt-0.5 flex-shrink-0" />
            ) : failed ? (
              <XCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
            ) : done ? (
              <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
            ) : (
              <Loader2 className="h-4 w-4 text-neutral-500 animate-spin mt-0.5 flex-shrink-0" />
            )}
            <div className="flex-1 min-w-0 overflow-hidden">
              <div className="text-xs font-medium text-neutral-800 dark:text-neutral-100 break-words">{baseTitle}</div>
              {body && (
                <div className="text-xs text-neutral-600 dark:text-neutral-400 mt-0.5 whitespace-pre-wrap break-words overflow-wrap-anywhere">
                  {body}
                </div>
              )}
            </div>
          </div>
        );
      })}
      {showDraftingCard && (
        <div className="flex items-start space-x-2 bg-neutral-100 dark:bg-neutral-800 rounded-lg px-3 py-2">
          <Loader2 className="h-4 w-4 text-neutral-500 animate-spin mt-0.5 flex-shrink-0" aria-hidden="true" />
          <div className="flex-1 min-w-0 overflow-hidden">
            <div className="text-xs font-medium text-neutral-800 dark:text-neutral-100 break-words">
              {t('drafting_answer')}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
